// TrustBadge — Marketplace 2.0 atomic trust signal.
// Spec: Playbook § 5.4 (Decision 4 — Variant B, spec-literal).
// Dumb component: parent supplies color + count. The Postgres `user_trust_stats`
// view that drives those values lands in Phase 2D.

import React from 'react';

const SIZES = {
  sm: { dot: 10, icon: 7, font: 11, gap: 4, role: 9 },
  md: { dot: 16, icon: 10, font: 13, gap: 6, role: 10 },
  lg: { dot: 24, icon: 14, font: 20, gap: 8, role: 9 },
};

const COLOR_MAP = {
  green:  { fill: 'var(--ok-2)', text: 'var(--chip-ok)', icon: '✓', label: 'clean record' },
  yellow: { fill: 'var(--amber)', text: 'var(--chip-warn)', icon: '⚠', label: 'some unresolved' },
  red:    { fill: '#ef4444', text: 'var(--chip-err)', icon: '✕', label: 'flagged' },
};

export default function TrustBadge({
  color,
  count,
  role,
  size = 'md',
  interactive = true,
  onClick,
}) {
  const s = SIZES[size] || SIZES.md;
  const roleLetter = role === 'seller' ? 'S' : 'B';
  const roleWord = role === 'seller' ? 'seller' : 'buyer';

  if (interactive && typeof onClick !== 'function') {
    // Playbook § 5.4: onClick is required when interactive.
    // Soft-warn rather than crash so storyboarding stays low-friction.
    // eslint-disable-next-line no-console
    console.warn('TrustBadge: interactive=true but no onClick provided.');
  }

  const cursor = interactive ? 'pointer' : 'default';

  // ─── Loading skeleton ─────────────────────────────────────────────
  // Same dimensions as a loaded badge to prevent layout shift.
  if (color === 'loading') {
    const barW = size === 'lg' ? 36 : (size === 'md' ? 22 : 16);
    return (
      <>
        <style>{TB_KEYFRAMES}</style>
        <span
          aria-label="loading trust badge"
          aria-busy="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: s.gap,
          }}
        >
          <span style={{
            width: s.dot,
            height: s.dot,
            borderRadius: '50%',
            background: 'rgba(var(--muted-5-rgb),0.3)',
            animation: 'tb-pulse 1.5s ease-in-out infinite',
            flexShrink: 0,
          }} />
          <span style={{
            width: barW,
            height: Math.max(8, s.font - 4),
            borderRadius: 2,
            background: 'rgba(var(--muted-5-rgb),0.3)',
            animation: 'tb-pulse 1.5s ease-in-out infinite',
          }} />
        </span>
      </>
    );
  }

  // ─── Sprout (0 transactions) ──────────────────────────────────────
  // Dashed border distinguishes "absence of behavior" from "value on the scale."
  if (color === 'sprout') {
    return (
      <>
        <style>{TB_KEYFRAMES}</style>
        <span
          role={interactive ? 'button' : undefined}
          tabIndex={interactive ? 0 : undefined}
          onClick={interactive ? onClick : undefined}
          onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick && onClick(); } } : undefined}
          aria-label={`new ${roleWord}, no transactions yet`}
          className={interactive ? 'tb-hover' : undefined}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: s.gap,
            padding: size === 'lg' ? '6px 12px 6px 7px' : '2px 6px 2px 4px',
            background: 'rgba(var(--ok-2-rgb),0.06)',
            border: '0.5px dashed rgba(var(--ok-2-rgb),0.4)',
            borderRadius: size === 'lg' ? 6 : 3,
            cursor,
            transition: 'border-color 0.15s ease, background 0.15s ease',
            userSelect: 'none',
          }}
        >
          <span style={{ fontSize: s.dot + 1, lineHeight: 1 }}>🌱</span>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: s.font,
            fontWeight: 500,
            color: 'var(--ok)',
          }}>
            {size === 'lg' ? 'new collector' : 'new'}
          </span>
        </span>
      </>
    );
  }

  // ─── Green / Yellow / Red ─────────────────────────────────────────
  const c = COLOR_MAP[color];
  if (!c) return null; // Unknown color — render nothing rather than crash.

  return (
    <>
      <style>{TB_KEYFRAMES}</style>
      <span
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? onClick : undefined}
        onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick && onClick(); } } : undefined}
        aria-label={`${roleWord} trust badge: ${count} lifetime ${roleWord} transactions, ${c.label}`}
        className={interactive ? 'tb-hover' : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: s.gap,
          padding: size === 'lg' ? '4px 8px 4px 6px' : '1px 4px 1px 2px',
          borderRadius: size === 'lg' ? 6 : 3,
          border: '0.5px solid transparent',
          cursor,
          transition: 'border-color 0.15s ease, background 0.15s ease',
          userSelect: 'none',
        }}
      >
        <span style={{
          width: s.dot,
          height: s.dot,
          background: c.fill,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: c.text,
          fontSize: s.icon,
          fontWeight: 700,
          flexShrink: 0,
          lineHeight: 1,
        }}>{c.icon}</span>

        {size === 'lg' ? (
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: s.font,
              fontWeight: 500,
              color: 'var(--text-bright)',
            }}>
              {count}<span style={{ opacity: 0.5, fontSize: s.font - 4 }}> {roleLetter}</span>
            </span>
            <span style={{
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: s.role,
              color: 'var(--muted-5)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>lifetime {roleWord}</span>
          </span>
        ) : (
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: s.font,
            fontWeight: 500,
            color: 'var(--text-bright)',
          }}>
            {count}<span style={{ opacity: 0.5 }}>{roleLetter}</span>
          </span>
        )}
      </span>
    </>
  );
}

// Skeleton pulse + hover treatment.
// Defined once; React + the browser dedupe identical <style> tags.
const TB_KEYFRAMES = `
  @keyframes tb-pulse {
    0%, 100% { opacity: 0.4; }
    50%      { opacity: 0.7; }
  }
  .tb-hover:hover {
    border-color: rgba(var(--wash-rgb),0.18) !important;
    background: rgba(var(--wash-rgb),0.03);
  }
  .tb-hover:focus-visible {
    outline: 2px solid rgba(var(--violet-rgb),0.6);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
